/**
 * Estil Home — water ripple / refraction effect.
 *
 * One continuous 2D wave-equation simulation, shared across every
 * "water" zone on the page, rendered through low-resolution canvases
 * (upscaled via CSS, which is what gives the soft translucent blur —
 * no separate blur filter needed). At rest everything is fully
 * transparent; the surface only becomes visible where the pointer (or
 * a touch) has actually disturbed it, and disturbances then spread and
 * fade on their own. A sweep plays once on load, travelling top to
 * bottom through the whole surface.
 *
 * WHY ONE SHARED SURFACE, NOT ONE PER SECTION
 *   Earlier this ran as an isolated simulation per section, boxed to
 *   that section's own element. Dragging the cursor across the seam
 *   between two "water" sections made the ripple visibly stop dead at
 *   the box edge, because there was no simulation data in the gap
 *   between them. This version builds a single surface spanning from
 *   the top of the first water zone to the bottom of the last one, so
 *   a ripple travels through the gap between sections exactly like it
 *   travels through the sections themselves — no seam.
 *
 * ZONES
 *   `[data-estil-ripple="true"]`       — a full-panel zone (text sits
 *                                        above the water; e.g. the hero
 *                                        text panel, the story section).
 *   `[data-estil-ripple-image="true"]` — an image zone (e.g. the hero
 *                                        photo). The water paints
 *                                        *above* the image here, but
 *                                        only as a soft ring near the
 *                                        image's own edges — a radial
 *                                        mask keeps the centre, where
 *                                        the product itself sits,
 *                                        completely untouched.
 *   Either attribute set to "false" carves that element's rectangle
 *   out of the surface — the simulation still runs underneath it, it
 *   simply never becomes visible there.
 *
 * HOW TO TURN THIS OFF
 *   1) Fastest, no code: in the Theme Customizer, open the section
 *      (Estil hero, or Estil story) and switch off "Water ripple
 *      effect" (hero has a separate "Water ripple effect — image"
 *      switch for the photo). Each zone has its own independent
 *      switch, and the shared surface only builds itself from
 *      whichever zones are currently on.
 *   2) Remove entirely: delete this file, its `<script>` tags, and the
 *      `data-estil-ripple` / `data-estil-ripple-image` attributes.
 *      Nothing else depends on any of them.
 *
 * Respects prefers-reduced-motion (the effect never runs at all when
 * set), pauses when the whole surface scrolls offscreen or the tab is
 * hidden, and rebuilds on resize and on Theme Editor section changes.
 * Safe to include this script more than once on a page — a load guard
 * makes every tag after the first a no-op.
 */
(function () {
  'use strict';

  if (window.__ESTIL_RIPPLE_LOADED__) return;
  window.__ESTIL_RIPPLE_LOADED__ = true;

  var REVEAL_SELECTOR = '[data-estil-ripple]';
  var IMAGE_SELECTOR = '[data-estil-ripple-image]';
  var CELL_PX = 11;
  var DAMPING = 0.986;
  var ENTRY_STRENGTH = 5.5;
  var DRAG_STRENGTH = 2.2;
  var LOAD_STRENGTH = 4.5;
  var LX = -0.6, LY = -0.65;

  var reducedMotionMQ = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function prefersReducedMotion() { return !!(reducedMotionMQ && reducedMotionMQ.matches); }

  var surface = null;
  var rebuildTimer = null;

  function rectToDoc(rect) {
    return { top: rect.top + window.scrollY, left: rect.left, width: rect.width, height: rect.height };
  }

  function teardown() {
    if (!surface) return;
    if (surface.rafId) cancelAnimationFrame(surface.rafId);
    if (surface.io) surface.io.disconnect();
    if (surface.behindCanvas && surface.behindCanvas.parentNode) surface.behindCanvas.parentNode.removeChild(surface.behindCanvas);
    surface.imageLayers.forEach(function (layer) {
      if (layer.canvas && layer.canvas.parentNode) layer.canvas.parentNode.removeChild(layer.canvas);
    });
    window.removeEventListener('pointermove', surface.onPointerMove);
    window.removeEventListener('pointerdown', surface.onPointerMove);
    window.removeEventListener('touchmove', surface.onTouchMove);
    surface = null;
  }

  function build() {
    teardown();
    if (prefersReducedMotion()) return;

    var revealEls = Array.prototype.slice.call(document.querySelectorAll(REVEAL_SELECTOR));
    var imageEls = Array.prototype.slice.call(document.querySelectorAll(IMAGE_SELECTOR));
    if (!revealEls.length && !imageEls.length) return;

    var top = Infinity, bottom = -Infinity;
    revealEls.concat(imageEls).forEach(function (el) {
      var r = rectToDoc(el.getBoundingClientRect());
      if (r.top < top) top = r.top;
      if (r.top + r.height > bottom) bottom = r.top + r.height;
    });
    if (!isFinite(top) || bottom - top < 10) return;

    var pageWidth = document.documentElement.clientWidth;
    var height = bottom - top;

    var nx = Math.min(240, Math.max(60, Math.round(pageWidth / CELL_PX)));
    var ny = Math.min(400, Math.max(50, Math.round(height / CELL_PX)));

    var cur = new Float32Array(nx * ny);
    var prev = new Float32Array(nx * ny);

    function toCellRect(rect) {
      var x0 = Math.max(0, Math.floor((rect.left / pageWidth) * nx));
      var x1 = Math.min(nx, Math.ceil(((rect.left + rect.width) / pageWidth) * nx));
      var y0 = Math.max(0, Math.floor(((rect.top - top) / height) * ny));
      var y1 = Math.min(ny, Math.ceil(((rect.top + rect.height - top) / height) * ny));
      return { x0: x0, x1: Math.max(x0 + 1, x1), y0: y0, y1: Math.max(y0 + 1, y1) };
    }

    var offZones = [];
    revealEls.forEach(function (el) {
      if (el.dataset.estilRipple === 'false') offZones.push(toCellRect(rectToDoc(el.getBoundingClientRect())));
    });

    var behindCanvas = document.createElement('canvas');
    behindCanvas.className = 'estil-ripple-surface';
    behindCanvas.setAttribute('aria-hidden', 'true');
    behindCanvas.style.cssText =
      'position:absolute; left:0; top:' + Math.round(top) + 'px; width:100%; height:' +
      Math.round(height) + 'px; z-index:0; pointer-events:none;';
    behindCanvas.width = nx;
    behindCanvas.height = ny;
    document.body.appendChild(behindCanvas);
    var ctxBehind = behindCanvas.getContext('2d', { alpha: true });
    var behindImg = ctxBehind.createImageData(nx, ny);
    (function () { var d = behindImg.data; for (var i = 3; i < d.length; i += 4) d[i] = 0; })();

    var imageLayers = [];
    imageEls.forEach(function (el) {
      if (el.dataset.estilRippleImage === 'false') return;
      var cells = toCellRect(rectToDoc(el.getBoundingClientRect()));
      var iw = Math.max(6, cells.x1 - cells.x0), ih = Math.max(6, cells.y1 - cells.y0);

      if (window.getComputedStyle(el).position === 'static') el.style.position = 'relative';

      var front = document.createElement('canvas');
      front.className = 'estil-ripple-surface estil-ripple-surface--image';
      front.setAttribute('aria-hidden', 'true');
      front.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; z-index:1; pointer-events:none;';
      front.style.maskImage = 'radial-gradient(ellipse at center, transparent 0%, transparent 58%, black 74%, black 84%, transparent 97%, transparent 100%)';
      front.style.webkitMaskImage = front.style.maskImage;
      front.width = iw;
      front.height = ih;
      el.appendChild(front);

      var fctx = front.getContext('2d', { alpha: true });
      var fimg = fctx.createImageData(iw, ih);
      (function () { var d = fimg.data; for (var i = 3; i < d.length; i += 4) d[i] = 0; })();

      imageLayers.push({ el: el, canvas: front, ctx: fctx, img: fimg, ox: cells.x0, oy: cells.y0, iw: iw, ih: ih });
    });

    surface = {
      top: top, bottom: bottom, pageWidth: pageWidth, height: height,
      nx: nx, ny: ny, cur: cur, prev: prev,
      behindCanvas: behindCanvas, ctxBehind: ctxBehind, behindImg: behindImg,
      offZones: offZones, imageLayers: imageLayers,
      running: false, rafId: null, io: null,
      lastGX: null, lastGY: null
    };

    step_bind(surface);
  }

  function inOffZone(s, x, y) {
    for (var i = 0; i < s.offZones.length; i++) {
      var z = s.offZones[i];
      if (x >= z.x0 && x < z.x1 && y >= z.y0 && y < z.y1) return true;
    }
    return false;
  }

  function step(s) {
    var nx = s.nx, ny = s.ny, cur = s.cur, prev = s.prev;
    for (var y = 1; y < ny - 1; y++) {
      var row = y * nx;
      for (var x = 1; x < nx - 1; x++) {
        var i = row + x;
        prev[i] = ((cur[i - 1] + cur[i + 1] + cur[i - nx] + cur[i + nx]) * 0.5 - prev[i]) * DAMPING;
      }
    }
    var t = s.cur; s.cur = s.prev; s.prev = t;
  }

  function disturb(s, gx, gy, strength) {
    var nx = s.nx, ny = s.ny, cur = s.cur;
    var xi = Math.round(gx), yi = Math.round(gy);
    for (var oy = -1; oy <= 1; oy++) {
      var yy = yi + oy;
      if (yy < 1 || yy > ny - 2) continue;
      for (var ox = -1; ox <= 1; ox++) {
        var xx = xi + ox;
        if (xx < 1 || xx > nx - 2) continue;
        var falloff = (ox === 0 && oy === 0) ? 1 : 0.45;
        cur[yy * nx + xx] += strength * falloff;
      }
    }
  }

  function renderBehind(s) {
    var nx = s.nx, ny = s.ny, cur = s.cur, data = s.behindImg.data, di = 0;
    for (var y = 0; y < ny; y++) {
      var yUp = Math.max(0, y - 1) * nx, yDn = Math.min(ny - 1, y + 1) * nx, row = y * nx;
      for (var x = 0; x < nx; x++) {
        if (inOffZone(s, x, y)) { data[di] = 0; data[di + 1] = 0; data[di + 2] = 0; data[di + 3] = 0; di += 4; continue; }
        var i = row + x;
        var xL = row + Math.max(0, x - 1), xR = row + Math.min(nx - 1, x + 1);
        var h = cur[i];
        var slopeX = cur[xR] - cur[xL];
        var slopeY = cur[yDn + x] - cur[yUp + x];
        var light = slopeX * LX + slopeY * LY;
        var mag = Math.min(1, Math.abs(h) / 6);
        var lit = Math.max(0, light) * 55;
        var shade = Math.max(0, -light) * 45;
        data[di] = Math.max(0, Math.min(255, 236 + lit - shade * 0.5));
        data[di + 1] = Math.max(0, Math.min(255, 228 + lit * 0.9 - shade * 0.7));
        data[di + 2] = Math.max(0, Math.min(255, 214 + lit * 0.6 - shade * 0.9));
        data[di + 3] = Math.max(0, Math.min(255, mag * 210));
        di += 4;
      }
    }
    s.ctxBehind.putImageData(s.behindImg, 0, 0);
  }

  function renderImageLayers(s) {
    for (var li = 0; li < s.imageLayers.length; li++) {
      var layer = s.imageLayers[li];
      var nx = s.nx, ny = s.ny, cur = s.cur, data = layer.img.data, di = 0;
      for (var y = 0; y < layer.ih; y++) {
        var gy = layer.oy + y;
        var yUp = Math.max(0, gy - 1) * nx, yDn = Math.min(ny - 1, gy + 1) * nx, row = gy * nx;
        for (var x = 0; x < layer.iw; x++) {
          var gx = layer.ox + x;
          var i = row + gx;
          var xL = row + Math.max(0, gx - 1), xR = row + Math.min(nx - 1, gx + 1);
          var h = cur[i] || 0;
          var slopeX = cur[xR] - cur[xL];
          var slopeY = cur[yDn + gx] - cur[yUp + gx];
          var light = slopeX * LX + slopeY * LY;
          var mag = Math.min(1, Math.abs(h) / 6);
          var lit = Math.max(0, light) * 60;
          var shade = Math.max(0, -light) * 50;
          data[di] = Math.max(0, Math.min(255, 246 + lit - shade * 0.5));
          data[di + 1] = Math.max(0, Math.min(255, 242 + lit * 0.9 - shade * 0.7));
          data[di + 2] = Math.max(0, Math.min(255, 234 + lit * 0.6 - shade * 0.9));
          data[di + 3] = Math.max(0, Math.min(235, mag * 235));
          di += 4;
        }
      }
      layer.ctx.putImageData(layer.img, 0, 0);
    }
  }

  function loop(s) {
    step(s);
    renderBehind(s);
    renderImageLayers(s);
    s.rafId = requestAnimationFrame(function () { loop(s); });
  }

  function toGrid(s, clientX, clientY) {
    var rect = s.behindCanvas.getBoundingClientRect();
    return {
      gx: ((clientX - rect.left) / rect.width) * s.nx,
      gy: ((clientY - rect.top) / rect.height) * s.ny
    };
  }

  function handleMove(s, clientX, clientY) {
    var p = toGrid(s, clientX, clientY);
    if (p.gx < 0 || p.gx > s.nx || p.gy < 0 || p.gy > s.ny) { s.lastGX = null; s.lastGY = null; return; }
    if (s.lastGX === null) { disturb(s, p.gx, p.gy, ENTRY_STRENGTH); s.lastGX = p.gx; s.lastGY = p.gy; return; }
    var dx = p.gx - s.lastGX, dy = p.gy - s.lastGY;
    var dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > 0.6) {
      var strength = Math.min(DRAG_STRENGTH * 1.8, DRAG_STRENGTH * (0.4 + dist / 3));
      disturb(s, p.gx, p.gy, strength);
      s.lastGX = p.gx; s.lastGY = p.gy;
    }
  }

  function step_bind(s) {
    var rafPending = false, pending = null;
    function queue(clientX, clientY) {
      pending = { x: clientX, y: clientY };
      if (!rafPending) {
        rafPending = true;
        requestAnimationFrame(function () { rafPending = false; if (pending) handleMove(s, pending.x, pending.y); });
      }
    }
    s.onPointerMove = function (e) { if (s.running) queue(e.clientX, e.clientY); };
    s.onTouchMove = function (e) {
      if (!s.running || !e.touches || !e.touches[0]) return;
      queue(e.touches[0].clientX, e.touches[0].clientY);
    };
    window.addEventListener('pointermove', s.onPointerMove, { passive: true });
    window.addEventListener('pointerdown', s.onPointerMove, { passive: true });
    window.addEventListener('touchmove', s.onTouchMove, { passive: true });

    if ('IntersectionObserver' in window) {
      s.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          s.running = entry.isIntersecting;
          if (s.running && !s.rafId) loop(s);
          if (!s.running && s.rafId) { cancelAnimationFrame(s.rafId); s.rafId = null; }
        });
      }, { threshold: 0.05 });
      s.io.observe(s.behindCanvas);
    } else {
      s.running = true;
      loop(s);
    }
  }

  function playEntrance() {
    var s = surface;
    if (!s || prefersReducedMotion()) return;

    var drops = Math.max(8, Math.min(26, Math.round(s.ny / 6)));
    var duration = Math.max(1800, Math.min(4200, s.ny * 18));
    var startY = s.ny * 0.04, endY = s.ny * 0.96;
    var centerX = s.nx * 0.5, wobble = s.nx * 0.07;

    for (var k = 0; k < drops; k++) {
      (function (k) {
        var t = k / (drops - 1);
        setTimeout(function () {
          if (!surface || surface !== s || prefersReducedMotion()) return;
          var eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
          var gy = startY + (endY - startY) * eased;
          var gx = centerX + Math.sin(t * Math.PI * 2.6) * wobble;
          disturb(s, gx, gy, LOAD_STRENGTH * (0.75 + Math.random() * 0.35));
        }, t * duration);
      })(k);
    }
  }

  function scheduleRebuild() {
    clearTimeout(rebuildTimer);
    rebuildTimer = setTimeout(function () { build(); }, 200);
  }

  function init() {
    build();
    window.addEventListener('load', function () { setTimeout(playEntrance, 220); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('resize', scheduleRebuild);
  document.addEventListener('visibilitychange', function () {
    if (!surface) return;
    if (document.hidden) { surface.running = false; if (surface.rafId) { cancelAnimationFrame(surface.rafId); surface.rafId = null; } }
    else { surface.running = true; if (!surface.rafId) loop(surface); }
  });
  // Theme Editor: rebuild whenever a section is added, removed or re-rendered
  // (this also picks up a merchant flipping one of the checkboxes live).
  document.addEventListener('shopify:section:load', scheduleRebuild);
  document.addEventListener('shopify:section:unload', scheduleRebuild);

  // Exposed for convenience (e.g. from the browser console) — the Theme
  // Customizer checkboxes are the intended on/off control.
  window.estilRipple = { rebuild: build, teardown: teardown };
})();
